import { useState, useCallback } from 'react';

export type CustomValidator = (
  field: string,
  value: string,
  setInvalidField: (identifier: string) => void,
  removeInvalidField: (identifier: string) => void,
) => void;

export const useFieldValidation = (validators?: CustomValidator[]) => {
  const [invalidFields, setInvalidField] = useState<string[]>([]);
  const [emptyFields, setEmptyField] = useState<string[]>([]);

  const removeInvalidField = useCallback((field: string) => {
    setInvalidField(prev => prev.filter(f => field !== f));
  }, []);

  const removeInvalidFields = useCallback((fields: string[]) => {
    setInvalidField(prev => prev.filter(f => !fields.includes(f)));
  }, []);

  const removeEmptyField = useCallback((field: string) => {
    setEmptyField(prev => prev.filter(f => field !== f));
  }, []);

  const removeEmptyFields = useCallback((fields: string[]) => {
    setEmptyField(prev => prev.filter(f => !fields.includes(f)));
  }, []);

  const validateField = useCallback(
    (field: string, value: string, required = true) => {
      if (['email'].includes(field) && required && !value?.length && !emptyFields.includes(field)) {
        setEmptyField([...emptyFields, field]);
        return;
      }
      if (required && !value?.length && !invalidFields.includes(field)) {
        setInvalidField([...invalidFields, field]);
        return;
      }

      if (validators && validators.length > 0) {
        if (['email'].includes(field) && value.length > 0 && emptyFields.includes(field)) {
          removeEmptyField(field);
        }
        validators.forEach((validator: CustomValidator) =>
          validator(
            field,
            value,
            (identifier: string) =>
              !invalidFields.includes(identifier) && setInvalidField([...invalidFields, identifier]),
            (identifier: string) => removeInvalidField(identifier),
          ),
        );
        return;
      }

      if (value.length > 0 && invalidFields.includes(field)) {
        removeInvalidField(field);
        return;
      }
      if (['email'].includes(field) && value.length > 0 && emptyFields.includes(field)) {
        removeEmptyField(field);
        return;
      }
    },
    [invalidFields, removeInvalidField, validators, emptyFields, removeEmptyField],
  );

  return {
    invalidFields,
    setInvalidField,
    validateField,
    removeInvalidField,
    removeInvalidFields,
    emptyFields,
    setEmptyField,
    removeEmptyField,
    removeEmptyFields,
  };
};
